<script setup lang="ts">
import { api } from '@/api';
import { ActionItemDto, JobOccurrenceDto, JobOccurrenceFilterOptionsDto } from '@/api/generator';
import { computed, ref, watch, onMounted, useTemplateRef } from 'vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useLoading } from '@/utils/useLoading';
import { buildOdataQuery } from '@/utils/odata';
import JobOccurrenceTable, {
  JobOccurrenceTableProps,
} from '@/views/jobOccurrences/components/JobOccurrenceTable/JobOccurrenceTable.vue';
import { routenames } from '@/router/routenames';
import { useRouter } from 'vue-router';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import DashboardFilter from './components/DashboardFilter.vue';
import { isActionEnabled, isActionVisible } from '@/utils/actions';
import Button from '@/components/buttons/Button.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import {
  getJobOccurrenceContextMenuActions,
  deleteJobOccurrence,
} from '@/views/jobOccurrences/utils/jobOccurrenceActions';
import { sortBy } from 'lodash';
import { useDashboardFilter } from './useDashboardFilter';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { useEntityEventStream } from '@/views/jobOccurrences/utils/useEntityEventHub';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';
import { useTabs } from '@/components/Tabs/useTabs';
import { TabDefinition } from '@/components/Tabs/types';
import Tabs from '@/components/Tabs/Tabs.vue';
import { defaultDashboardFilter } from './defaults';
import { DashboardJobGroupOption } from './types';
import { JobExecutionUrlAction } from '../jobOccurrences/types';
import { useLatestApiRequest } from '@/views/jobOccurrences/utils/useLatestApiRequest';

const router = useRouter();

const { t } = useTranslation();

const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const jobOccurrences = ref<JobOccurrenceDto[]>();
const filterOptions = ref<JobOccurrenceFilterOptionsDto>();
const itemsLength = ref<number>();
const jobOccurrencesRequest = useLatestApiRequest({
  request: api.jobOccurrencesApi.getJobOccurrences.bind(api.jobOccurrencesApi),
});
const filterOptionsRequest = useLatestApiRequest({
  request: api.jobOccurrencesApi.getFilterOptions.bind(api.jobOccurrencesApi),
});

const { filterObject, filterOptionsFilter, dataFilter, onUpdatePagination, updatePagination } =
  useDashboardFilter();

const { onEntityEvent } = useEntityEventStream();

const tabs = computed(
  () =>
    ({
      open: { title: t('JobGroup.open'), hideContentSheet: true },
      review: { title: t('JobGroup.review'), hideContentSheet: true },
    }) satisfies TabDefinition,
);

const { activeTab, selectByKey } = useTabs({
  tabs,
  initialTab: filterObject.value.stateGroup,
});

const {
  action: loadJobOccurrences,
  loading,
  actionWithoutLoading,
  loadingFirstTime,
} = useLoading(async () => {
  const query = buildOdataQuery(dataFilter.value);

  const { result } = await jobOccurrencesRequest.call(query);

  if (result?.value) {
    itemsLength.value = result.odataCount;
    jobOccurrences.value = result.value;
  }
}, true);

onUpdatePagination(loadJobOccurrences);

// Add pagination initialization for server side pagination
onMounted(() => {
  updatePagination({
    page: 1,
    itemsPerPage: 20,
    sortBy: [{ key: 'start', order: 'asc' }],
  });
});

onEntityEvent(async () => {
  await updateFilterOptions();
  // Keep loader active if a visible table load is already in progress
  if (loading.value) {
    await loadJobOccurrences();
    return;
  }

  await actionWithoutLoading();
});

const tableOptions = computed<Partial<JobOccurrenceTableProps>>(() => {
  switch (filterObject.value.stateGroup) {
    case 'open':
      return {
        columns: [
          'jobConfigurationName',
          'targetSystem.name',
          'frequency',
          'state',
          'progress',
          'contextMenu',
        ],
        defaultSorting: [{ key: 'plannedPeriod' }],
      };
    case 'review':
    default:
      return {
        columns: [
          'jobConfigurationName',
          'targetSystem.name',
          'frequency',
          'state',
          'progress',
          'contextMenu',
        ],
        defaultSorting: [{ key: 'completedByUser.name' }],
      };
  }
});

async function updateFilterOptions() {
  const { result } = await filterOptionsRequest.call(buildOdataQuery(filterOptionsFilter.value));
  // Only set filter options if a result has a value (for aborted calls, this would not be the case).
  if (result?.value?.[0]) {
    filterOptions.value = result.value[0];
  }
}

watch(() => filterOptionsFilter.value, updateFilterOptions, {
  immediate: true,
});

watch(
  () => filterObject.value,
  (newFilter, oldFilter) => {
    if (!oldFilter || newFilter.stateGroup === oldFilter.stateGroup) {
      loadJobOccurrences();
    }
  },
);

watch(
  () => filterObject.value.stateGroup,
  (stateGroup) => {
    if (stateGroup && activeTab.value !== stateGroup) {
      selectByKey(stateGroup);
    }
  },
);

function executionTooltip(item: JobOccurrenceDto) {
  const action = item.possibleActions?.find((a) => a.action === ActionItemDto.Execute);
  return action?.reason
    ? t(action.reason.key as I18nKey, action.reason.parameters || {})
    : undefined;
}

async function startAction(item: JobOccurrenceDto, action: JobExecutionUrlAction) {
  const hash: string = '#tab:workitems';

  await router.push({
    name: routenames.jobOccurrences.execution,
    hash,
    params: {
      id: item.id,
      action,
    },
  });
}

function getExecutionBtnText(jobOccurrence: JobOccurrenceDto) {
  switch (jobOccurrence.state) {
    case 'Paused':
    case 'ReworkRequested':
    case 'InExecution':
      return t('JobExecutionContinue');
    case 'ReviewPending':
    case 'InReview':
      return t('JobExecutionReopen');
    case 'New':
    default:
      return t('JobExecutionStart');
  }
}

function getExecutionBtnVariant(jobOccurrence: JobOccurrenceDto) {
  return jobOccurrence.state === 'ReviewPending' ? 'secondary' : 'primary';
}

const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async (jobOccurrence: JobOccurrenceDto) => {
    const success = await deleteJobOccurrence({
      ...jobOccurrence,
    });
    if (success) {
      await loadJobOccurrences();
    }
  },
});

function getContextMenuActions(item: JobOccurrenceDto) {
  return getJobOccurrenceContextMenuActions(item, router, t, deleteAction);
}

function handleTabChange(newTab: string) {
  filterObject.value = {
    ...defaultDashboardFilter,
    stateGroup: newTab as DashboardJobGroupOption,
  };
  selectByKey(newTab as DashboardJobGroupOption);
}

// Sort function that prioritizes ReviewPending first, then InReview
const sortedJobOccurrences = computed(() => {
  const jobs = jobOccurrences.value ?? [];

  switch (filterObject.value.stateGroup) {
    case 'open':
      return sortBy(jobs, 'end');
    case 'review':
      return sortBy(jobs, 'editingTime');
    default:
      return jobs;
  }
});
</script>

<template>
  <BasePage :loading="loadingFirstTime" data-testid="DashboardView">
    <template #title>
      <BasePageTitle :title="$t('Dashboard')" />
    </template>

    <v-toolbar class="pageTabs bg-primary-lighten-5 mt-2" height="48">
      <Tabs
        :modelValue="filterObject.stateGroup"
        :tabs="tabs"
        withShadow
        class="w-100"
        @update:modelValue="handleTabChange"
      />
    </v-toolbar>

    <DashboardFilter v-model="filterObject" :filterOptions="filterOptions" />

    <div ref="table">
      <JobOccurrenceTable
        :jobOccurrences="sortedJobOccurrences ?? []"
        :columns="tableOptions.columns"
        :defaultSorting="tableOptions.defaultSorting"
        :loading="loading"
        :hiddenColumns="['start', 'assigned', 'review']"
        :activeTab="filterObject.stateGroup"
        :hideDueBadge="filterObject.stateGroup === 'review'"
        :serverSidePagination="true"
        :itemsLength="itemsLength"
        :sortable="false"
        :height="height"
        fixedHeader
        @update:options="updatePagination"
      >
        <template #actions="{ item: jobOccurrence }">
          <div class="d-flex justify-end w-100">
            <Tooltip
              v-if="isActionVisible(jobOccurrence, 'Review')"
              position="top"
              :style="{ maxWidth: '10rem' }"
              :disabled="
                jobOccurrence.state === 'InReview'
                  ? false
                  : isActionEnabled(jobOccurrence, 'Review')
              "
            >
              <template #target="{ props: targetProps }">
                <div class="w-100" v-bind="targetProps">
                  <Button
                    :disabled="
                      jobOccurrence.state === 'InReview'
                        ? true
                        : !isActionEnabled(jobOccurrence, 'Review')
                    "
                    class="w-100"
                    :label="t('JobExecutionReview')"
                    variant="primary"
                    size="small"
                    @click.stop="startAction(jobOccurrence, 'startReview')"
                  />
                </div>
              </template>
              <template #tooltip>{{
                jobOccurrence.state === 'InReview'
                  ? t('StateOfUseEnum.InReview')
                  : executionTooltip(jobOccurrence)
              }}</template>
            </Tooltip>

            <Tooltip
              v-if="
                isActionVisible(jobOccurrence, 'Execute') && jobOccurrence.state !== 'ReviewPending'
              "
              position="top"
              class="ml-2"
              :style="{ maxWidth: '10rem' }"
              :disabled="
                jobOccurrence.state === 'InReview'
                  ? false
                  : isActionEnabled(jobOccurrence, 'Execute')
              "
            >
              <template #target="{ props: targetProps }">
                <div class="w-100 mr-4" v-bind="targetProps">
                  <Button
                    :disabled="
                      jobOccurrence.state === 'InReview'
                        ? true
                        : !isActionEnabled(jobOccurrence, 'Execute')
                    "
                    class="w-100"
                    :label="getExecutionBtnText(jobOccurrence)"
                    :variant="getExecutionBtnVariant(jobOccurrence)"
                    size="small"
                    @click.stop="startAction(jobOccurrence, 'startExecution')"
                  />
                </div>
              </template>
              <template #tooltip>{{
                jobOccurrence.state === 'InReview'
                  ? t('StateOfUseEnum.InReview')
                  : executionTooltip(jobOccurrence)
              }}</template>
            </Tooltip>

            <ContextMenu
              :actions="getContextMenuActions(jobOccurrence)"
              :closeOnScrollElements="tableScrollElement"
            />
          </div>
        </template>
      </JobOccurrenceTable>
    </div>
  </BasePage>
</template>

<style scoped lang="scss">
:deep(.pageContent) {
  overflow-y: hidden !important;
}
</style>
